import { ChevronLeft, ChevronRight, Eye, Pencil } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { useAppSelector } from '../../../redux/store/hooks';
import {
  useGetFileFromS3Mutation,
  useUploadFileToS3Mutation
} from '../../../services/CloudApi';
import {
  useGetIsUnderReviewMutation,
  useUpdateQuestionStatusMutation
} from '../../../services/dataReviewerApi/dataReviewerApi';
import { renderHtmlWithTeX } from '../../../utils/RenderQuestionWithTex';
import { viewPresignedUrlInNewTab } from '../../../utils/utili';
import Button from '../../Common/Button';
import CommonDialog from '../../Common/Dialog';
import Accordion from '../../Common/Disclosure';

import CmsProgramming from './CmsProgramming';

const ApprovedTab = () => {
  const { domainData, moduleData, questionTypeData } = useAppSelector(
    state => state.root.questionBank
  );
  const {
    underReviewAndRejectedData,
    selectedSource,
    selectedSection,
    selectedTopic,
    selectedQuestionType,
    searchString
  } = useAppSelector(state => state.root.dataReviewer);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [comment, setComment] = useState<string | undefined>();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState('');
  const [editFile, setEditFile] = useState<File | null>(null);
  const [selectedQId, setSelectedQId] = useState('');

  const [getIsUnderReview] = useGetIsUnderReviewMutation();
  const [updateQuestionStatus] = useUpdateQuestionStatusMutation();
  const [getFileFromS3] = useGetFileFromS3Mutation();
  const [uploadFile] = useUploadFileToS3Mutation();

  useEffect(() => {
    getIsUnderReview({
      questionStatus: 4003,
      page: 0,
      size: 10,
      sourceId: selectedSource?.id ?? '',
      topicId: selectedTopic?.id ?? '',
      sectionId: selectedSection?.id ?? '',
      questionTypeId: selectedQuestionType?.id ?? '',
      searchString
    });
    setPageSize(10);
  }, [
    selectedSource,
    selectedSection,
    selectedTopic,
    selectedQuestionType,
    getIsUnderReview,
    searchString
  ]);

  const getQuestionNumber = (index: number) => {
    return index + 1;
  };
  const getName = (question: any) => {
    const selectedDomain = domainData?.data?.find(
      domain => domain.id === question.domain
    );
    const selectedModule = moduleData?.data?.find(
      module => module.id === question.moduleId
    );
    const selectedQuestionType = questionTypeData?.data?.find(
      type => type.id === question.questionType
    );

    return {
      domainName: selectedDomain?.dominName || '-',
      moduleName: selectedModule?.moduleName || '-',
      questionTypeName: selectedQuestionType?.questionTypeName || '-'
    };
  };
  const handlePageChange = (newPage: number) => {
    getIsUnderReview({
      questionStatus: 4003,
      page: newPage,
      size: pageSize,
      sourceId: selectedSource?.id ?? '',
      topicId: selectedTopic?.id ?? '',
      sectionId: selectedSection?.id ?? '',
      questionTypeId: selectedQuestionType?.id ?? '',
      searchString
    });
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    getIsUnderReview({
      questionStatus: 4003,
      page: 0,
      size: Number(event.target.value),
      sourceId: selectedSource?.id ?? '',
      topicId: selectedTopic?.id ?? '',
      sectionId: selectedSection?.id ?? '',
      questionTypeId: selectedQuestionType?.id ?? '',
      searchString
    });
    setPageSize(Number(event.target.value));
    setCurrentPage(0);
  };

  const getReadableFileName = (name: string) => {
    if (!name) return '';

    const maxLength = 30;

    if (name.length <= maxLength) return name;

    const ext = name.split('.').pop();
    const base = name.substring(0, maxLength - (ext?.length || 0) - 3);
    return `${base}...${ext}`;
  };

  const handleViewAttachment = async (question: any) => {
    const attachmentUrl =
      question?.additionalProps?.validationAttachment?.fileName;
    if (!attachmentUrl) return;
    try {
      const url = await getFileFromS3(attachmentUrl).unwrap();
      viewPresignedUrlInNewTab(url);
    } catch (error) {
      toast.error('Failed to open attachment');
    }
  };

  const handleUploadFile = async (e: any) => {
    if (!e) return;
    try {
      const response = await uploadFile(e).unwrap();
      const s3file = response?.data;
      return s3file;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const handleEditSubmit = async () => {
    try {
      let s3FileData;
      if (editFile) {
        s3FileData = await handleUploadFile(editFile);
        if (!s3FileData) {
          toast.error('File upload failed');
          return;
        }
      }
      const payload: any = {
        questionId: selectedQId,
        status: 4003,
        comment
      };
      if (s3FileData) {
        payload.s3UploadedDocumentDTO = s3FileData;
      }
      await updateQuestionStatus(payload).unwrap();
      setEditFile(null);
      setIsEditModalOpen(false);
      toast.success('File updated successfully');
      getIsUnderReview({
        questionStatus: 4003,
        page: currentPage,
        size: pageSize,
        sourceId: selectedSource?.id ?? '',
        topicId: selectedTopic?.id ?? '',
        questionTypeId: selectedQuestionType?.id ?? '',
        sectionId: selectedSection?.id ?? '',
        searchString
      });
    } catch (err) {
      toast.error('Failed to update file');
    }
  };

  const renderPagination = () => {
    const totalPages = Math.ceil(
      (underReviewAndRejectedData?.totalquestions || 0) / pageSize
    );
    const currentPageGroup = Math.floor(currentPage / 10);
    const pages = Array.from(
      { length: Math.min(10, totalPages - currentPageGroup * 10) },
      (_, i) => currentPageGroup * 10 + i
    );

    return (
      <div className='my-3 flex items-center justify-center gap-2'>
        <div className='flex items-center gap-1'>
          <Button
            label={<ChevronLeft size={20} />}
            variant='none'
            classes='rounded-full hover:bg-gray-100 mr-1'
            handleClickBtn={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 0}
          />
          {pages.map(page => (
            <Button
              key={page}
              label={page + 1}
              variant='none'
              classes={`h-8 w-8 rounded-full ${
                currentPage === page
                  ? 'bg-primary text-white'
                  : 'hover:bg-gray-100'
              }`}
              handleClickBtn={() => handlePageChange(page)}
            />
          ))}

          <Button
            label={<ChevronRight size={20} />}
            variant='none'
            classes='rounded-full hover:bg-gray-100'
            handleClickBtn={() => handlePageChange(currentPage + 1)}
            disabled={currentPage >= totalPages - 1}
          />
        </div>
        <select
          value={pageSize}
          onChange={handlePageSizeChange}
          className='cursor-pointer rounded-md border border-gray-300 px-2 py-1'
        >
          {[10, 20, 50, 100].map(size => (
            <option key={size} value={size}>
              {size} per page
            </option>
          ))}
        </select>
        <p>TOtal Questions : {underReviewAndRejectedData?.totalquestions}</p>
      </div>
    );
  };
  return (
    <div className='mb-32 h-full w-full overflow-y-auto pb-8'>
      {underReviewAndRejectedData?.data?.map((item, questionIndex) => (
        <div key={item.id} className='mr-4 px-6 py-2'>
          <Accordion
            width='w-full'
            title={`${currentPage * pageSize + questionIndex + 1}. ${item.questionText}`}
            isOpen
            onToggle={() => {
              // Handle accordion toggle logic here
            }}
          >
            <div>
              <div className='flex gap-2'>
                {getQuestionNumber(questionIndex)}.{' '}
                <pre className='whitespace-pre-wrap'>
                  {renderHtmlWithTeX(item?.questionText, true)}
                </pre>
              </div>
              <pre className='whitespace-pre-wrap'>{item?.readingPassage}</pre>
              <div className='flex w-full'>
                <p className='mt-3 w-1/2'>
                  <span className='font-bold'>ID</span>: {item?.id}
                </p>
                <p className='mt-3 w-1/2'>
                  <span className='font-bold'>Sub Topic</span>: {item?.subTopic}
                </p>
              </div>
              <p>
                <span className='font-bold'>Difficulty: </span>
                {item?.difficultyLevel}
              </p>
            </div>
            {!item?.isProgrammingQuestion && (
              <>
                <div className='mt-3'>
                  <span className='font-bold'>Options:</span>
                  {item?.options?.map((each: any, index: number) => (
                    <p key={each?.answerOption}>
                      <span className='font-bold'>{index + 1}: </span>
                      <span>
                        {renderHtmlWithTeX(each?.answerOption, false)}
                      </span>
                    </p>
                  ))}
                </div>
                <p className='my-2 text-green-600'>
                  <span className='font-bold'>Correct Answer:</span>{' '}
                  {renderHtmlWithTeX(item?.correctAnswer, false)}
                </p>
                <p className='my-2'>
                  <span className='font-bold'>Explanation:</span>{' '}
                  {renderHtmlWithTeX(item?.explanation, true)}
                </p>
                <div>
                  <p>
                    <span className='font-bold'>Created Date</span>:{' '}
                    {item?.createdDate}
                  </p>
                </div>
                {/* <div className='my-4 flex gap-2'>
              <Button
                handleClickBtn={() => handleClickApprove(item.id)}
                label='Approve'
                variant='contained'
                classes='!mx-0'
              />
              <Button
                handleClickBtn={() => handleClickReject(item.id)}
                label='Reject'
                variant='outlined-danger'
              />
            </div> */}
              </>
            )}
            {item?.isProgrammingQuestion && <CmsProgramming question={item} />}
            {item.additionalProps?.validationAttachment && (
              <div className='mt-3 w-1/2'>
                <p className='my-2'>
                  <span className='font-bold'>File Attachment:</span>
                </p>
                <div className='flex items-center justify-between rounded-lg border border-blue-600 px-3 py-2'>
                  <span className='text-md flex-1 truncate text-gray-700'>
                    {getReadableFileName(
                      item?.additionalProps?.validationAttachment?.fileName
                    )}
                  </span>

                  <div className='flex items-center gap-3'>
                    <Eye
                      size={20}
                      className='cursor-pointer text-gray-600 hover:text-black'
                      onClick={() => handleViewAttachment(item)}
                    />

                    <Pencil
                      size={20}
                      className='cursor-pointer text-gray-600 hover:text-black'
                      onClick={() => {
                        setSelectedQId(item?.id);
                        setComment(item?.comment);
                        setSelectedFileName(
                          item?.additionalProps?.validationAttachment?.fileName
                        );
                        setEditFile(null);
                        setIsEditModalOpen(true);
                      }}
                    />

                    {/* 
                      <DownloadIcon
                        size={20}
                        className='cursor-pointer text-gray-600 hover:text-black'
                        onClick={() => {
                          downloadAttachment(item)
                        }}
                      />
                  */}
                  </div>
                </div>
              </div>
            )}
          </Accordion>
        </div>
      ))}
      {underReviewAndRejectedData?.data?.length > 0 && (
        <div
          style={{ width: 'calc(100% - 208px)' }}
          className='fixed bottom-0 flex justify-center border-t border-[#E3E5E9] bg-white shadow-md'
        >
          {renderPagination()}
        </div>
      )}
      <CommonDialog
        isOpen={isEditModalOpen}
        close={() => setIsEditModalOpen(false)}
        dialogTitle={'Edit File Attachment'}
        childClasses='px-0 pb-0 pt-0'
        width='w-[40%] min-h-[250px]'
      >
        <div className='px-6 py-4'>
          <div className='mt-2'>
            <label className='font-medium'>Upload File</label>

            <div className='mt-2 flex items-center gap-2'>
              <label className='cursor-pointer rounded border bg-gray-100 px-3 py-1 hover:bg-gray-200'>
                Choose File
                <input
                  type='file'
                  className='hidden'
                  onChange={e => setEditFile(e.target.files?.[0] || null)}
                />
              </label>

              {/* File Name Display */}
              <span
                className='flex-1 truncate text-sm text-gray-600'
                title={editFile ? editFile.name : selectedFileName}
              >
                {editFile
                  ? editFile.name
                  : selectedFileName || 'No file chosen'}
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className='fixed bottom-0 right-0 flex items-center justify-end gap-2 p-6'>
            <Button
              label='Submit'
              variant='contained'
              handleClickBtn={() => {
                handleEditSubmit();
              }}
            />
            <Button
              label='Cancel'
              variant='outlined'
              handleClickBtn={() => setIsEditModalOpen(false)}
            />
          </div>
        </div>
      </CommonDialog>
      {underReviewAndRejectedData?.data?.length === 0 && (
        <div className='mt-8 flex h-full items-center justify-center'>
          <p>No Data Found</p>
        </div>
      )}
    </div>
  );
};

export default ApprovedTab;
